import React, { useMemo, useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Modal, Select, DatePicker, Button, Spin } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import Table from "../../../core/pagination/datatable";
import GRAN_LOGO1 from "../Logo/Granules_logo1.png";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../Config/Config";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditLogReport = () => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(0);
  // Initialize dateRange with dayjs objects
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"), // 7 days ago
    dayjs(), // Current date and time
  ]);
  const [users, setUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const user = JSON.parse(sessionStorage.getItem("user")) || {
    UserName: "Unknown User",
    UserID: null,
  };

  // Static report configuration
  const reportConfig = useMemo(() => {
    const now = dayjs();
    const year = now.format("YYYY");
    const month = now.format("MM");
    const day = now.format("DD");
    const hours = now.format("HH");
    const minutes = now.format("mm");
    const seconds = now.format("ss");
    return {
      ID: "barcode",
      title: "Audit Trial Report",
      filename: `AuditTrialReport_${year}-${month}-${day}_${hours}-${minutes}-${seconds}`,
      columns: [
        {
          title: "Date & Time",
          key: "PerformedOn",
          render: (text) => {
                if (!text) return "NA";
                const date = new Date(text);
                const pad = (n) => (n < 10 ? "0" + n : n);
                const day = pad(date.getUTCDate());
                const month = pad(date.getUTCMonth() + 1);
                const year = date.getUTCFullYear();
                const hours = pad(date.getUTCHours());
                const minutes = pad(date.getUTCMinutes());
                const formatted = `${day}/${month}/${year} ${hours}:${minutes}`;
                return formatted.startsWith("01/01/1900") ? "NA" : formatted;
              },
        },
        { title: "User ID", key: "username" },
        {
          title: "Description",
          key: "Notes",
          render: (text) => {
            if (!text) return "";
            let result = "";
            let line = "";
            text.split(" ").forEach((word) => {
              if ((line + word).length > 50) {
                result += line.trim() + "\n";
                line = "";
              }
              line += word + " ";
            });
            return (
              <span style={{ whiteSpace: "pre-line" }}>
                {(result + line).trim()}
              </span>
            );
          },
        },
        {
          title: "Status",
          key: "IsSucces",
          render: (value) => (value ? "Success" : "Failed"),
        },
      ],
      showEquipmentId: false,
    };
  }, []);

  // Fetch audit log data
  const fetchAuditLogData = async () => {
    setLoading(true);
    try {
      const start = dateRange[0];
      const end = dateRange[1];

      const formatDateTime = (date) => {
        return date.format("YYYY-MM-DD HH:mm:ss");
      };

      const startDate = formatDateTime(start);
      const endDate = formatDateTime(end);
      const response = await fetch(
        `${getApiBaseUrl()}/api/getAuditReport?PerformedBy=${selectedUserId}&startdate=${encodeURIComponent(
          startDate
        )}&enddate=${encodeURIComponent(endDate)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (data.Status && Array.isArray(data.ResultData)) {
        setDataSource(data.ResultData);
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error("Error fetching audit log data:", error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/getAllUsers`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => setUsers(data?.ResultData || []))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    fetchAuditLogData();
  }, [selectedUserId, dateRange]);

  const tableColumns = useMemo(
    () =>
      reportConfig.columns.map((col) => ({
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        render: col.render || ((text) => text || "-"),
        sorter: (a, b) => {
          const valA = a[col.key] ?? "";
          const valB = b[col.key] ?? "";
          return typeof valA === "number"
            ? valA - valB
            : String(valA).localeCompare(String(valB));
        },
      })),
    [reportConfig]
  );

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format("DD/MM/YYYY HH:mm");
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const logoWidth = 15;
    const logoHeight = 15;
    const pageWidth = doc.internal.pageSize.getWidth();

    const generateHeader = () => {
      doc.addImage(GRAN_LOGO1, "PNG", 10, 10, logoWidth, logoHeight);
      doc.setFontSize(11);
      doc.text("Granules India Limited", 30, 14);
      doc.text("Gagillapur", 30, 20);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Title: ${reportConfig.title}`, 10, 35);
      doc.setFont(undefined, "normal");
      return 42;
    };

    const startY = generateHeader();

    autoTable(doc, {
      startY: startY,
      head: [reportConfig.columns.map((col) => col.title)],
      body:
        dataSource.length > 0
          ? dataSource.map((item) =>
              reportConfig.columns.map((col) => {
                if (col.key === "Notes") {
                  const notes = item[col.key];
                  if (!notes) return "";
                  let result = "";
                  let line = "";
                  notes.split(" ").forEach((word) => {
                    if ((line + word).length > 50) {
                      result += line.trim() + "\n";
                      line = "";
                    }
                    line += word + " ";
                  });
                  return (result + line).trim();
                } else if (
                  col.key === "PerformedOn" &&
                  col.title === "Date & Time"
                ) {
                  return formatDate(item[col.key]);
                } else {
                  return col.render
                    ? col.render(item[col.key])
                    : item[col.key] ?? "";
                }
              })
            )
          : [
              [
                {
                  content: "No Data Available",
                  colSpan: reportConfig.columns.length,
                  styles: { halign: "center" },
                },
              ],
            ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
      margin: { top: startY, bottom: 35 },
      didDrawPage: () => {
        generateHeader();
      },
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - 40,
        doc.internal.pageSize.height - 10
      );
      if (i === totalPages) {
        const pageHeight = doc.internal.pageSize.height;
        const footerY = pageHeight - 20;
        doc.text("Remarks:", 10, footerY - 10);
        doc.text(`Printed by: ${user.UserName}`, 10, footerY);
        const startFormatted = dateRange[0].format("DD-MM-YYYY HH:mm");
        const endFormatted = dateRange[1].format("DD-MM-YYYY HH:mm");
        doc.text(
          `Date Range: ${startFormatted} - ${endFormatted}`,
          10,
          footerY + 6
        );
        const now = dayjs();
        doc.text(
          `Printed on: ${now.format("DD-MM-YYYY HH:mm:ss")}`,
          10,
          footerY + 12
        );
      }
    }

    const pdfBlob = doc.output("blob");

    const formData = new FormData();
    formData.append("file", pdfBlob, `${reportConfig.filename}.pdf`);

    fetch(`${getApiBaseUrl()}/api/uploadReportCopy`, {
      method: "POST",
      body: formData,
      headers: {},
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Upload success:", data);
      })
      .catch((err) => {
        console.error("Upload failed:", err);
      });

    doc.save(`${reportConfig.filename}.pdf`);
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setAuthLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;
    let loginSuccessful = false;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const loginData = await loginRes.json();
      if (loginData?.Message === "Login successful") {
        loginSuccessful = true;
      } else {
        setPasswordError(
          "Reauthentication failed. Please check your password."
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      setPasswordError("Something went wrong. Please try again.");
    }

    try {
      const activityPayload = {
        ActivityType: 1,
        PerformedBy: user?.UserID,
        PerformedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        Notes: loginSuccessful
          ? `${reportConfig.title} downloaded by ${username}`
          : `Failed Reauthentication for ${reportConfig.title} by ${username}`,
        Location: "Reports",
        IsSucces: loginSuccessful ? 1 : 0,
      };

      const actRes = await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activityPayload),
      });

      const actData = await actRes.json();
      if (!actData?.success) {
        console.warn("Activity log failed");
      }
    } catch (err) {
      console.error("Activity log error:", err);
    } finally {
      setAuthLoading(false);
      if (loginSuccessful) {
        setVisible(false);
        setPassword("");
        handleExportPDF();
      }
    }
  };

  const handleUserChange = (value) => {
    setSelectedUserId(value);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]); // Store as dayjs objects
    } else {
      setDateRange([dayjs().subtract(7, "day"), dayjs()]); // Reset to default if cleared
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/assetmgt/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item" aria-current="page">
                    {reportConfig.title}
                  </h4>
                </ol>
              </nav>
              <h6>Manage your barcode reports</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderTooltip}>
                <Link
                  to="#"
                  onClick={() => {
                    setVisible(true);
                    setPassword("");
                    setPasswordError("");
                  }}
                >
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
        <div className="card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set"></div>
            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap gap-3">
              <div style={{ width: 200 }}>
                <Select
                  style={{ width: "100%" }}
                  value={selectedUserId === 0 ? 0 : selectedUserId}
                  onChange={handleUserChange}
                  placeholder="Select Username"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option key={0} value={0}>
                    All
                  </Option>
                  {users?.map((user) => (
                    <Option key={user?.UserID} value={user?.UserID}>
                      {user?.Username}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <RangePicker
                  showTime={{ format: "HH:mm" }}
                  value={dateRange} // Controlled by dateRange state
                  onChange={handleDateRangeChange}
                  format="DD-MM-YYYY HH:mm"
                  style={{ width: 350 }}
                />
                <Button
                  type="primary"
                  onClick={fetchAuditLogData}
                  loading={loading}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <div className="text-center my-4">
                  <Spin size="large" />
                  <p>Loading data...</p>
                </div>
              ) : dataSource?.length > 0 ? (
                <Table columns={tableColumns} dataSource={dataSource} />
              ) : (
                <p className="text-center my-4">No data available</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Confirm Password"
        open={visible}
        onCancel={() => {
          setVisible(false);
          setPassword("");
          setPasswordError("");
        }}
        onOk={handlePasswordSubmit}
        confirmLoading={authLoading}
        okText="Verify & Download"
        width={350}
      >
        <div className="mb-3">
          <div className="pass-group">
            <input
              type={showPassword ? "text" : "password"}
              className={`form-control ${passwordError ? "is-invalid" : ""}`}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
              autoComplete="new-password"
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            <span
              className={`ti toggle-password ${
                showPassword ? "ti-eye" : "ti-eye-off"
              }`}
              onClick={() => setShowPassword((prev) => !prev)}
            ></span>
          </div>
          {passwordError && (
            <div className="invalid-feedback" style={{ display: "block" }}>
              {passwordError}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AuditLogReport;